import { Link } from 'react-router-dom';

export default function Footer() {
  return (
    <footer className="bg-gray-100 dark:bg-[#060A14] border-t border-gray-200 dark:border-[#2D3148] pt-20 pb-8">
      <div className="max-w-[1280px] mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Column 1 - Brand */}
          <div>
            <div className="mb-4">
              <div className="text-2xl font-bold">
                <span className="text-gray-900 dark:text-white">Job</span>
                <span className="text-[#6366F1] font-mono">OS</span>
              </div>
            </div>
            <p className="text-gray-500 dark:text-[#94A3B8] text-sm mb-3">One OS. Every job.</p>
            <p className="text-gray-400 dark:text-[#64748B] text-sm mb-4">jobos.dev</p>
            <p className="text-gray-400 dark:text-[#475569] text-xs">© 2026 JobOS. All rights reserved.</p>
          </div>

          {/* Column 2 - Product */}
          <div>
            <h4 className="text-gray-900 dark:text-white text-base font-semibold mb-6">Product</h4>
            <ul className="space-y-3">
              {[
                { label: 'Features', href: '#features' },
                { label: 'How It Works', href: '#how-it-works' },
                { label: 'Pricing', href: '#pricing' },
                { label: 'Chrome Extension', href: '#extension' },
                { label: 'Changelog', href: 'https://app.jobos.dev/changelog', external: true },
                { label: 'Roadmap', href: 'https://app.jobos.dev/roadmap', external: true }
              ].map((link, index) => (
                <li key={index}>
                  {link.href.startsWith('#') ? (
                    <button onClick={() => { const el = document.getElementById(link.href.replace('#','')); if(el) el.scrollIntoView({behavior:'smooth'}); }} className="text-gray-500 dark:text-[#94A3B8] text-sm hover:text-gray-900 dark:hover:text-[#F8FAFC] transition-colors duration-200 cursor-pointer">
                      {link.label}
                    </button>
                  ) : (
                    <a href={link.href} target="_blank" rel="noopener noreferrer" className="text-gray-500 dark:text-[#94A3B8] text-sm hover:text-gray-900 dark:hover:text-[#F8FAFC] transition-colors duration-200 cursor-pointer">{link.label}</a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3 - Resources */}
          <div>
            <h4 className="text-gray-900 dark:text-white text-base font-semibold mb-6">Resources</h4>
            <ul className="space-y-3">
              {[
                { label: 'Documentation', href: 'https://app.jobos.dev/docs', external: true },
                { label: 'Blog', href: '/blog', external: false },
                { label: 'Support Center', href: '/support', external: false },
                { label: 'Help Center', href: 'https://app.jobos.dev/help', external: true },
                { label: 'Supported ATS Platforms', href: '/ats-platforms', external: false },
                { label: 'Supported Job Boards', href: 'https://app.jobos.dev/job-boards', external: true },
                { label: 'Privacy Policy', href: '/privacy', external: false },
                { label: 'Terms of Service', href: '/terms', external: false }
              ].map((link, index) => (
                <li key={index}>
                  {link.external ? (
                    <a href={link.href} target="_blank" rel="noopener noreferrer" className="text-gray-500 dark:text-[#94A3B8] text-sm hover:text-gray-900 dark:hover:text-[#F8FAFC] transition-colors duration-200 cursor-pointer">{link.label}</a>
                  ) : (
                    <Link to={link.href} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="text-gray-500 dark:text-[#94A3B8] text-sm hover:text-gray-900 dark:hover:text-[#F8FAFC] transition-colors duration-200 cursor-pointer">{link.label}</Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4 - Connect */}
          <div>
            <h4 className="text-gray-900 dark:text-white text-base font-semibold mb-6">Connect</h4>
            <ul className="space-y-3 mb-6">
              {[
                { label: 'Twitter/X: @JobosDev', href: 'https://x.com/JobosDev', icon: 'ri-twitter-x-line' },
                { label: 'LinkedIn', href: 'https://www.linkedin.com/in/jobosdev', icon: 'ri-linkedin-fill' },
                { label: 'GitHub', href: 'https://github.com/jobosdev', icon: 'ri-github-fill' },
                { label: 'Email: hello@jobos.dev', href: 'mailto:hello@jobos.dev', icon: 'ri-mail-line' }
              ].map((link, index) => (
                <li key={index}>
                  <a href={link.href} target="_blank" rel="noopener noreferrer" className="text-gray-500 dark:text-[#94A3B8] text-sm hover:text-gray-900 dark:hover:text-[#F8FAFC] transition-colors duration-200 cursor-pointer flex items-center gap-2">
                    <i className={`${link.icon} text-base`}></i>
                    <span>{link.label}</span>
                  </a>
                </li>
              ))}
            </ul>
            <a href="https://chrome.google.com/webstore" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 h-10 px-6 bg-[#6366F1] text-white text-sm font-semibold rounded-full transition-all duration-200 hover:bg-[#4F46E5] hover:-translate-y-0.5 whitespace-nowrap cursor-pointer">
              <span>⚡</span> Get Extension
            </a>
          </div>
        </div>

        <div className="pt-8 border-t border-gray-200 dark:border-[#2D3148]">
          <p className="text-gray-400 dark:text-[#475569] text-xs text-center leading-relaxed mb-3">
            Built for job seekers around the world
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {[
              { flag: 'https://flagcdn.com/w40/us.png', name: 'US' },
              { flag: 'https://flagcdn.com/w40/gb.png', name: 'UK' },
              { flag: 'https://flagcdn.com/w40/ca.png', name: 'Canada' },
              { flag: 'https://flagcdn.com/w40/au.png', name: 'Australia' },
              { flag: 'https://flagcdn.com/w40/ae.png', name: 'UAE' },
              { flag: 'https://flagcdn.com/w40/in.png', name: 'India' },
              { flag: 'https://flagcdn.com/w40/pk.png', name: 'Pakistan' },
              { flag: 'https://flagcdn.com/w40/de.png', name: 'Germany' },
              { flag: 'https://flagcdn.com/w40/fr.png', name: 'France' },
              { flag: 'https://flagcdn.com/w40/sg.png', name: 'Singapore' },
            ].map((country) => (
              <div key={country.name} className="flex items-center gap-1.5 text-gray-400 dark:text-[#475569] text-xs">
                <img
                  src={country.flag}
                  alt={country.name}
                  title={country.name}
                  className="w-5 h-3.5 object-cover rounded-sm shadow-sm"
                />
                <span>{country.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}